export const ROUTES = {
  root: '/',
  upgrade: '/upgrade',
  config: '/config',
  datasets: '/datasets',
  eval: '/eval',
  evalCreate: '/eval/create',
  evalConfigs: '/eval/configs',
  evalResultTags: '/evals/result-tags',
  evals: '/evals',
  providers: '/providers',
  providerEdit: '/providers/:id',
  prompts: '/prompts',
  guardrails: {
    index: '/guardrails',
    dashboard: '/guardrails/dashboard',
    list: '/guardrails/list',
    requests: '/guardrails/requests',
    detail: '/guardrails/:id',
    integrate: '/guardrails/:id/integrate',
    generatePolicies: '/guardrails/:id/generate-policies',
    targets: '/guardrails/targets',
    targetDetail: '/guardrails/targets/:id',
  },
  jobs: '/jobs',
  adminJobs: '/admin/jobs',
  loggedInPasswordReset: '/logged-in-reset-password',
  changelog: '/changelog',
  login: '/login',
  oauthCallback: '/auth/fusionauth/callback',
  logout: '/logout',
  apiTokens: '/api-tokens',
  register: '/register',
  noAccess: '/no-access',
  noTeam: '/no-team',
  notFound: '/not-found',
  organization: {
    index: '/organizations',
    details: '/organizations/:orgId',
    users: '/organizations/:orgId/users',
    teams: '/organizations/:orgId/teams',
    team: {
      details: '/organizations/:orgId/teams/:teamId',
      members: '/organizations/:orgId/teams/:teamId/members',
    },
    roles: {
      index: '/organizations/:orgId/roles',
      details: '/organizations/:orgId/roles/:roleId',
    },
    new: '/organizations/new',
    usage: '/organizations/:orgId/usage',
    probeUsage: '/organizations/:orgId/probe-usage',
    resultTags: '/organizations/:orgId/result-tags',
    overview: '/organizations/:orgId/overview',
  },
  passwordReset: {
    confirm: '/reset-password/confirm',
    request: '/reset-password',
  },
  redteam: {
    index: '/redteam',
    configs: '/redteam/configs',
    createConfig: '/redteam/configs/create',
    // TODO: Nest these routes under plugins
    plugins: '/redteam/plugins',
    teamProvider: '/redteam/provider',
    pluginSeverityOverrides: '/redteam/plugins/severities',
    customPolicies: '/redteam/plugins/policies',
    externalGuidelines: '/redteam/external-guidelines',
    externalGuidelineDetail: '/redteam/external-guidelines/:id',
    gradingGuidance: '/redteam/plugins/grading-guidance',
    gradingExamples: '/redteam/target/:targetId/gradingExamples',
    libraryPolicy: '/library/policies/:policyId',
    report: '/redteam/report',
    remediationReport: '/redteam/report/:id/remediation',
    targets: '/redteam/targets',
    targetSetup: '/redteam/targets/setup',
    editTarget: '/redteam/targets/:targetId',
    vulnerabilities: '/redteam/vulnerabilities',
    createScan: '/redteam/scan/create',
    target: {
      base: '/redteam/target/:targetId',
      dashboard: '/redteam/target/:targetId/dashboard',
      settings: '/redteam/target/:targetId/settings',
      vulnerabilities: '/redteam/target/:targetId/vulnerabilities',
      history: '/redteam/target/:targetId/history',
      gradingExamples: '/redteam/target/:targetId/gradingExamples',
      pluginSeverities: '/redteam/target/:targetId/pluginSeverities',
    },
  },
  serverSettings: {
    edit: '/server/edit',
    index: '/server',
    redteamProvider: '/server/edit/redteamProvider',
    teamRedteamProvider: '/server/team-provider',
    gradingProvider: '/server/edit/gradingProvider',
    guardrailsProvider: '/server/edit/guardrailsProvider',
    multilingualProvider: '/server/edit/multilingualProvider',
    agentProvider: '/server/edit/agentProvider',
    redteamGenerationProvider: '/server/edit/redteamGenerationProvider',
  },
  modelAudit: '/model-audit',
  modelAuditConfiguration: '/model-audit/configuration',
  modelAuditScanDetails: '/model-audit/scan/:scanId',
  modelAuditHistory: '/model-audit/history',
  reports: {
    index: '/reports',
    teamActivity: '/reports/team-activity',
    vulnerabilityPosture: '/reports/vulnerability-posture',
  },
  codeScan: {
    setup: '/code-scan/setup',
    feedback: '/code-scan/feedback/:feedbackToken',
  },
  setup: '/setup',
  verifyEmail: '/verify-email',
  welcome: '/welcome',
} as const;

export const EVAL_ID_SCAN_PATTERN = /\s+from scan:\s+(eval-[\w-:.]+)$/;

type Leaves<T, L0 = T[keyof T]> = L0 extends Record<string, unknown> ? Leaves<L0> : L0;

export const PAGE_TITLES_BY_ROUTE: Record<Leaves<typeof ROUTES>, string> = {
  [ROUTES.root]: 'Home',
  [ROUTES.upgrade]: 'Upgrade',
  [ROUTES.config]: 'Configuration',
  [ROUTES.datasets]: 'Datasets',
  [ROUTES.eval]: 'Eval',
  [ROUTES.evalCreate]: 'Create Eval',
  [ROUTES.evalConfigs]: 'Eval Configs',
  [ROUTES.evalResultTags]: 'Result Tags',
  [ROUTES.evals]: 'Evals',
  [ROUTES.providers]: 'Providers',
  [ROUTES.providerEdit]: 'Edit Provider',
  [ROUTES.prompts]: 'Prompts',
  [ROUTES.guardrails.index]: 'Guardrails Dashboard',
  [ROUTES.guardrails.dashboard]: 'Guardrails Dashboard',
  [ROUTES.guardrails.list]: 'Guardrails',
  [ROUTES.guardrails.requests]: 'Request History',
  [ROUTES.guardrails.detail]: 'Guardrail Details',
  [ROUTES.guardrails.integrate]: 'Guardrail Integration',
  [ROUTES.guardrails.generatePolicies]: 'Generate Policies',
  [ROUTES.guardrails.targets]: 'Targets',
  [ROUTES.guardrails.targetDetail]: 'Target Details',
  [ROUTES.jobs]: 'Redteam / Scan History',
  [ROUTES.adminJobs]: 'Admin Jobs',
  [ROUTES.loggedInPasswordReset]: 'Reset Password',
  [ROUTES.changelog]: 'Changelog',
  [ROUTES.login]: 'Login',
  [ROUTES.oauthCallback]: 'Sign in',
  [ROUTES.logout]: 'Logout',
  [ROUTES.apiTokens]: 'API Tokens',
  [ROUTES.register]: 'Register',
  [ROUTES.noAccess]: 'Access Denied',
  [ROUTES.noTeam]: 'No Team',
  [ROUTES.notFound]: 'Page Not Found',

  // Organization routes
  [ROUTES.organization.index]: 'Organizations',
  [ROUTES.organization.details]: 'Organization Details',
  [ROUTES.organization.users]: 'Users',
  [ROUTES.organization.teams]: 'Teams',
  [ROUTES.organization.team.details]: 'Team Details',
  [ROUTES.organization.team.members]: 'Team Members',
  [ROUTES.organization.roles.index]: 'Roles',
  [ROUTES.organization.roles.details]: 'Role Details',
  [ROUTES.organization.new]: 'Create Organization',
  [ROUTES.organization.usage]: 'Usage',
  [ROUTES.organization.probeUsage]: 'Probe Usage',
  [ROUTES.organization.resultTags]: 'Result Tags',
  [ROUTES.organization.overview]: 'Organization Overview',

  // Password reset routes
  [ROUTES.passwordReset.confirm]: 'Confirm Password Reset',
  [ROUTES.passwordReset.request]: 'Request Password Reset',

  // Redteam routes
  [ROUTES.redteam.index]: 'Redteam',
  [ROUTES.redteam.configs]: 'Scan Templates',
  [ROUTES.redteam.createConfig]: 'Create Scan Template',
  [ROUTES.redteam.plugins]: 'Plugins Collection',
  [ROUTES.redteam.teamProvider]: 'Team Inference Providers',
  [ROUTES.redteam.pluginSeverityOverrides]: 'Plugin Severity Overrides',
  [ROUTES.redteam.customPolicies]: 'Policy Library',
  [ROUTES.redteam.externalGuidelines]: 'Grading Guidelines',
  [ROUTES.redteam.externalGuidelineDetail]: 'Guideline Details',
  [ROUTES.redteam.gradingGuidance]: 'Grading Guidelines',
  [ROUTES.redteam.gradingExamples]: 'Grading Examples',
  [ROUTES.redteam.libraryPolicy]: 'Policy Details',
  [ROUTES.redteam.report]: 'Report',
  [ROUTES.redteam.remediationReport]: 'Remediation Report',
  [ROUTES.redteam.targets]: 'Targets',
  [ROUTES.redteam.targetSetup]: 'Target Setup',
  [ROUTES.redteam.editTarget]: 'Edit Target',
  [ROUTES.redteam.vulnerabilities]: 'Vulnerabilities',
  [ROUTES.redteam.createScan]: 'Create Scan',

  // Redteam target routes
  [ROUTES.redteam.target.base]: 'Target',
  [ROUTES.redteam.target.dashboard]: 'Target Dashboard',
  [ROUTES.redteam.target.settings]: 'Target Settings',
  [ROUTES.redteam.target.vulnerabilities]: 'Target Vulnerabilities',
  [ROUTES.redteam.target.history]: 'Target History',
  [ROUTES.redteam.target.pluginSeverities]: 'Target Plugin Severities',

  // Server settings routes
  [ROUTES.serverSettings.index]: 'Server Settings',
  [ROUTES.serverSettings.edit]: 'Edit Server Settings',
  [ROUTES.serverSettings.redteamProvider]: 'Default Red Team Provider',
  [ROUTES.serverSettings.teamRedteamProvider]: 'Team-level Red Team Provider',
  [ROUTES.serverSettings.gradingProvider]: 'Grading Provider',
  [ROUTES.serverSettings.guardrailsProvider]: 'Guardrails Provider',
  [ROUTES.serverSettings.multilingualProvider]: 'Multilingual Provider',
  [ROUTES.serverSettings.agentProvider]: 'Agent Provider',
  [ROUTES.serverSettings.redteamGenerationProvider]: 'Test Generation Provider',

  // Reports
  [ROUTES.reports.index]: 'Reports',
  [ROUTES.reports.teamActivity]: 'Team Activity',
  [ROUTES.reports.vulnerabilityPosture]: 'Vulnerability Posture',

  // Model audit routes
  [ROUTES.modelAudit]: 'Model Audit',
  [ROUTES.modelAuditConfiguration]: 'Model Audit Configuration',
  [ROUTES.modelAuditScanDetails]: 'Model Audit Scan Details',
  [ROUTES.modelAuditHistory]: 'Model Audit History',

  // Code Scan routes
  [ROUTES.codeScan.setup]: 'Code Scan Setup',
  [ROUTES.codeScan.feedback]: 'Code Scan Feedback',

  // Setup
  [ROUTES.setup]: 'Setup',

  // Other routes
  [ROUTES.verifyEmail]: 'Verify Email',
  [ROUTES.welcome]: 'Authenticate',
};

// Metadata keys that should be hidden from the user in the UI
export const HIDDEN_METADATA_KEYS = ['citations', '_promptfooFileMetadata', '__promptfoo'];
